import { Info } from "lucide-react";
import { useState } from "react";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { type SignInConsentItem, useSignInApprovals } from "@/lib/api";
import { logger } from "@/lib/logger";

import { ApprovalTable } from "./approval-table";
import { MarkApprovedDialog } from "./mark-approved-dialog";
import { SendTestEmailButton } from "./send-test-email-button";

/**
 * The IT approval queue: every project's own sign-in app registration that
 * still needs a directory admin's tenant-wide consent.
 *
 * Scaffold UI cannot grant that consent itself, by design — this page is the
 * human loop around it. Once IT has consented, through the link or in the
 * Entra portal, the server confirms it from the grant by itself within a few
 * minutes; Check and Mark approved are only the fallbacks for when it can't.
 */
export function AdminItApprovalsPage() {
  const [includeConsented, setIncludeConsented] = useState(false);
  const {
    data: items = [],
    isLoading,
    isError,
    isPlaceholderData,
  } = useSignInApprovals(includeConsented);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [markItem, setMarkItem] = useState<SignInConsentItem | null>(null);

  // `reported` is still waiting: nobody has confirmed the grant exists.
  const pendingCount = items.filter((item) => item.consent_state !== "consented").length;

  const toggleIncludeConsented = (checked: boolean) => {
    logger.debug("[it-approvals] show approved:", checked);
    setIncludeConsented(checked);
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div>
        <h2 className="text-primary text-2xl font-normal">IT approvals</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Sign-in app registrations waiting for a directory admin &mdash; {pendingCount} waiting
        </p>
      </div>

      <div className="border-border bg-muted/40 mt-6 flex items-start gap-2 rounded-lg border p-3">
        <Info className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        <div className="text-muted-foreground space-y-1 text-xs">
          <p>
            A project with its own sign-in has one app registration per environment, and each needs
            tenant-wide admin consent from a directory admin before anyone can sign in to it.
          </p>
          <p>
            Open the link as a Global Administrator, Cloud Application Administrator or Privileged
            Role Administrator and accept. Links expire after an hour &mdash; reload the page for a
            fresh one.
          </p>
          <p>
            This page normally confirms the approval by itself within a few minutes, also when
            consent was granted in the Entra portal. Check and Mark approved are only for when it
            doesn&apos;t.
          </p>
        </div>
      </div>

      <div className="bg-card border-border mt-6 overflow-hidden rounded-lg border">
        <div className="border-border flex items-center justify-between gap-2 border-b px-4 py-3">
          <SendTestEmailButton />
          <div className="flex items-center gap-2">
            <Label htmlFor="it-approvals-show-approved" className="text-sm font-normal">
              Show approved
            </Label>
            <Switch
              id="it-approvals-show-approved"
              checked={includeConsented}
              onCheckedChange={toggleIncludeConsented}
              disabled={isPlaceholderData}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground py-16 text-center text-sm">
            Loading approvals...
          </div>
        ) : isError ? (
          <div className="text-destructive py-16 text-center text-sm">
            Could not load the IT approval queue.
          </div>
        ) : (
          <ApprovalTable
            items={items}
            includeConsented={includeConsented}
            expandedId={expandedId}
            onToggleExpanded={(requestId) =>
              setExpandedId((current) => (current === requestId ? null : requestId))
            }
            onMarkApproved={setMarkItem}
          />
        )}
      </div>

      <MarkApprovedDialog
        item={markItem}
        open={markItem !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMarkItem(null);
          }
        }}
      />
    </div>
  );
}
